/**
 * Shared UI for demo scenes: persistent backdrop, browser chrome, synthetic
 * cursor, text blocks with the house entrance, and decode-gated images.
 */
import React, { useEffect, useState } from 'react'
import { AbsoluteFill, continueRender, delayRender, useCurrentFrame, useVideoConfig } from 'remotion'
import { clamp01, easeOutExpo } from '@/lib/easing'
import { alpha, demoFonts, type DemoPalette } from './theme'

const GRAIN =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix type='saturate' values='0'/></filter><rect width='160' height='160' filter='url(#n)' opacity='0.5'/></svg>`,
  )

/**
 * Atmosphere behind every scene. timeOffsetSec carries the clip's global
 * timeline position so the glow drift is continuous across scene cuts — the
 * backdrop reads as one persistent space even though each clip remounts it.
 */
export function Backdrop({ pal, timeOffsetSec = 0 }: { pal: DemoPalette; timeOffsetSec?: number }) {
  const frame = useCurrentFrame()
  const { fps, width, height } = useVideoConfig()
  const t = timeOffsetSec + frame / fps
  const ox = Math.sin(t * 0.11) * width * 0.06
  const oy = Math.cos(t * 0.09) * height * 0.05
  return (
    <AbsoluteFill style={{ backgroundColor: pal.bg, overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: '-20%',
          background: [
            `radial-gradient(42% 36% at ${28 + ox / 14}% ${18 + oy / 10}%, ${alpha(pal.accent, pal.dark ? 0.16 : 0.12)}, transparent 70%)`,
            `radial-gradient(50% 44% at ${78 - ox / 12}% ${82 - oy / 12}%, ${alpha(pal.accent2, pal.dark ? 0.10 : 0.08)}, transparent 70%)`,
            `radial-gradient(70% 60% at 50% 118%, ${alpha(pal.accent, pal.dark ? 0.07 : 0.05)}, transparent 70%)`,
          ].join(','),
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${alpha(pal.dark ? '#ffffff' : '#000000', pal.dark ? 0.13 : 0.10)} 1px, transparent 1.4px)`,
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(75% 65% at 50% 42%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(75% 65% at 50% 42%, black 30%, transparent 100%)',
          opacity: 0.55,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("${GRAIN}")`,
          opacity: pal.dark ? 0.05 : 0.035,
          mixBlendMode: 'overlay',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(120% 100% at 50% 50%, transparent 55%, ${alpha('#000000', pal.dark ? 0.5 : 0.14)} 100%)`,
        }}
      />
    </AbsoluteFill>
  )
}

export const CHROME_BAR_H = 52

/** macOS-style browser window; children fill the content area below the bar. */
export function BrowserChrome({
  pal,
  url,
  width,
  height,
  children,
}: {
  pal: DemoPalette
  url?: string
  width: number
  height: number
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 18,
        background: pal.chromeBg,
        border: `1px solid ${pal.chromeBorder}`,
        boxShadow: `0 1px 0 ${alpha('#ffffff', 0.06)} inset, 0 40px 90px -28px ${alpha('#000000', 0.55)}, 0 12px 30px -12px ${alpha('#000000', 0.4)}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: CHROME_BAR_H,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 8,
          borderBottom: `1px solid ${pal.chromeBorder}`,
          position: 'relative',
        }}
      >
        {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
          <div key={c} style={{ width: 13, height: 13, borderRadius: 7, background: c }} />
        ))}
        {url ? (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: alpha(pal.dark ? '#ffffff' : '#000000', 0.07),
              borderRadius: 9,
              padding: '7px 18px',
              maxWidth: '55%',
            }}
          >
            <svg width="12" height="13" viewBox="0 0 12 13" style={{ flexShrink: 0, opacity: 0.6 }}>
              <path
                d="M3 5V4a3 3 0 1 1 6 0v1h.5A1.5 1.5 0 0 1 11 6.5v4A1.5 1.5 0 0 1 9.5 12h-7A1.5 1.5 0 0 1 1 10.5v-4A1.5 1.5 0 0 1 2.5 5H3Zm1.4 0h3.2V4a1.6 1.6 0 1 0-3.2 0v1Z"
                fill={pal.urlText}
              />
            </svg>
            <span
              style={{
                fontFamily: demoFonts.mono,
                fontSize: 14.5,
                color: pal.urlText,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {url}
            </span>
          </div>
        ) : null}
      </div>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>{children}</div>
    </div>
  )
}

/** Synthetic macOS cursor in frame space, with click ripple and press dip. */
export function Cursor({
  x,
  y,
  press,
  click,
  accent,
}: {
  x: number
  y: number
  press: number
  click: number
  accent: string
}) {
  const ringR = 14 + (1 - click) * 38
  return (
    <div style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
      {click > 0 ? (
        <div
          style={{
            position: 'absolute',
            left: x - ringR,
            top: y - ringR,
            width: ringR * 2,
            height: ringR * 2,
            borderRadius: '50%',
            border: `2.5px solid ${alpha(accent, click * 0.9)}`,
            background: alpha(accent, click * 0.12),
          }}
        />
      ) : null}
      <svg
        width={30}
        height={34}
        viewBox="0 0 26 30"
        style={{
          position: 'absolute',
          left: x - 4,
          top: y - 3,
          transform: `scale(${press})`,
          transformOrigin: '4px 3px',
          filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.45))',
        }}
      >
        <path
          d="M4.5 2.5 L4.5 22.5 L9.6 17.9 L12.9 25.6 L16.7 24.0 L13.4 16.5 L20.5 16.0 Z"
          fill="#ffffff"
          stroke="#1c1c1e"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

/** House entrance: blur-to-sharp easeOutExpo rise, staggered by delaySec. */
export function riseStyle(frame: number, fps: number, delaySec: number, dist = 26) {
  const t = easeOutExpo(clamp01((frame / fps - delaySec) / 0.85))
  return {
    opacity: t,
    transform: `translateY(${(1 - t) * dist}px)`,
    filter: `blur(${(1 - t) * 7}px)`,
  }
}

/** Eyebrow label + headline + sub with staggered entrance. */
export function TextBlock({
  pal,
  label,
  headline,
  sub,
  size = 'md',
  delaySec = 0.15,
}: {
  pal: DemoPalette
  label?: string
  headline?: string
  sub?: string
  size?: 'md' | 'lg'
  delaySec?: number
}) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const headlineSize = size === 'lg' ? 108 : 56
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: size === 'lg' ? 26 : 14 }}>
      {label ? (
        <div style={riseStyle(frame, fps, delaySec)}>
          <span
            style={{
              fontFamily: demoFonts.mono,
              fontSize: size === 'lg' ? 17 : 15,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: pal.accent,
              background: alpha(pal.accent, 0.10),
              border: `1px solid ${alpha(pal.accent, 0.30)}`,
              borderRadius: 999,
              padding: '8px 16px',
            }}
          >
            {label}
          </span>
        </div>
      ) : null}
      {headline ? (
        <h1
          style={{
            ...riseStyle(frame, fps, delaySec + 0.12),
            fontFamily: demoFonts.headline,
            fontWeight: 650,
            fontSize: headlineSize,
            lineHeight: 1.02,
            letterSpacing: '-0.03em',
            color: pal.text,
            margin: 0,
            maxWidth: size === 'lg' ? 1280 : 760,
          }}
        >
          {headline}
        </h1>
      ) : null}
      {sub ? (
        <p
          style={{
            ...riseStyle(frame, fps, delaySec + 0.24),
            fontFamily: demoFonts.serif,
            fontStyle: 'italic',
            fontSize: size === 'lg' ? 34 : 25,
            color: pal.textDim,
            margin: 0,
            maxWidth: size === 'lg' ? 900 : 640,
            lineHeight: 1.3,
          }}
        >
          {sub}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Image that blocks the frame until fully decoded. Img's load event fires
 * before rasterization finishes on very large screenshots (full-page captures
 * are 10k+ px tall), which surfaces as black frames in renders — decode()
 * resolves only when the bitmap is paint-ready.
 */
export function DecodedImg({ src, style }: { src: string; style?: React.CSSProperties }) {
  const [handle] = useState(() => delayRender(`decode ${src}`))
  useEffect(() => {
    let cancelled = false
    const img = new Image()
    img.src = src
    img
      .decode()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) continueRender(handle)
      })
    return () => {
      cancelled = true
      continueRender(handle)
    }
  }, [src, handle])
  return <img src={src} style={style} draggable={false} />
}
